import React from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { PROOF_ENFORCEMENT_MODES } from "../../constants/proofEnforcementModes";
import {
  PROOF_APPROVAL_MODES,
  normalizeProofApprovalMode,
} from "../../constants/proofApprovalModes";

export const proofStatusLabel = (status) => ({
  missing: "Proof missing",
  awaiting_approval: "Awaiting proof approval",
  approved: "Approved",
  not_required: "Not required",
  unresolved: "Charge configuration could not be resolved",
  missing_or_unapproved: "No approved proof found",
}[status] || "Proof needs attention");

const proofCountText = (count) => `${count} required proof${count === 1 ? "" : "s"}`;
const isCompletePackageValidation = (validation) =>
  normalizeProofApprovalMode(validation?.approvalMode) === PROOF_APPROVAL_MODES.COMPLETE_PACKAGE;
const lifecycleLabel = (proof = {}) => proof.readinessLabel || proofStatusLabel(proof.proofStatus);
const lifecycleDetail = (proof = {}) => {
  if (proof.readinessDetail) return proof.readinessDetail;
  if (proof.proofStatus === "missing") return "A proof has not been attached for this required decoration charge.";
  if (proof.proofStatus === "awaiting_approval") return "A proof exists but has not been approved yet.";
  if (proof.proofStatus === "unresolved") return "The proof requirement for this decoration charge could not be resolved.";
  if (proof.proofStatus === "approved") return "Internal approval complete · Customer approval complete";
  return "No approved proof was found for this order.";
};

const buildSummaryLine = ({ validation }) => {
  const summary = validation?.summary || {};
  if (!summary.requiredCount) return "No proofs are required for this order.";
  return `${summary.approvedCount || 0} of ${proofCountText(summary.requiredCount)} approved`;
};

const buildLifecycleSummary = ({ validation }) => {
  const rows = (Array.isArray(validation?.proofs) ? validation.proofs : [])
    .filter((proof) => proof?.required && !proof?.satisfied);
  if (!rows.length) return null;

  const counts = rows.reduce((acc, proof) => {
    const key = proof?.readinessStatus || proof?.proofStatus || "unknown";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const parts = [];
  if (counts.proof_not_uploaded || counts.missing) {
    const count = counts.proof_not_uploaded || counts.missing;
    parts.push(`${count} proof${count === 1 ? "" : "s"} not uploaded`);
  }
  if (counts.awaiting_customer_approval) {
    parts.push(`${counts.awaiting_customer_approval} awaiting customer approval`);
  }
  if (counts.awaiting_internal_approval) {
    parts.push(`${counts.awaiting_internal_approval} awaiting internal approval`);
  }
  if (counts.awaiting_approvals || counts.awaiting_approval) {
    const count = (counts.awaiting_approvals || 0) + (counts.awaiting_approval || 0);
    parts.push(`${count} awaiting internal and customer approval`);
  }
  if (counts.rejected) {
    parts.push(`${counts.rejected} rejected`);
  }
  if (counts.unresolved) {
    parts.push(`${counts.unresolved} unresolved`);
  }
  return parts.length ? `${parts.join(". ")}.` : null;
};

const getRequiredProofRows = (validation) =>
  (Array.isArray(validation?.proofs) ? validation.proofs : [])
    .filter((proof) => proof?.required);

const countArtworkLocations = (validation) => getRequiredProofRows(validation).length;

const artworkLocationText = (count) => `${count} artwork location${count === 1 ? "" : "s"}`;

const getPackageLifecycleRow = (validation) => {
  const rows = getRequiredProofRows(validation);
  return rows.find((proof) => proof?.readinessStatus === "rejected") ||
    rows.find((proof) => proof?.readinessStatus === "proof_not_uploaded" || proof?.proofStatus === "missing") ||
    rows.find((proof) => proof?.readinessStatus === "awaiting_customer_approval") ||
    rows.find((proof) => proof?.readinessStatus === "awaiting_internal_approval") ||
    rows.find((proof) => proof?.readinessStatus === "awaiting_approvals" || proof?.proofStatus === "awaiting_approval") ||
    rows.find((proof) => proof?.readinessStatus === "approved" || proof?.proofStatus === "approved") ||
    rows[0] ||
    null;
};

const buildPackagePolicyLine = ({ mode, validation }) => {
  const count = countArtworkLocations(validation);
  if (!count) return null;
  if (validation?.valid) {
    return mode === PROOF_ENFORCEMENT_MODES.AT_LEAST_ONE
      ? "The current complete-package proof requirement has been satisfied."
      : "The complete proof package is approved.";
  }
  if (mode === PROOF_ENFORCEMENT_MODES.AT_LEAST_ONE) {
    return "At least one qualifying approval is required before Purchase Orders can be generated.";
  }
  return "The complete proof package must be approved before Purchase Orders can be generated.";
};

const buildPackageStatusLine = (validation) => {
  const row = getPackageLifecycleRow(validation);
  const count = countArtworkLocations(validation);
  if (!row || !count) return null;
  if (row.readinessStatus === "proof_not_uploaded" || row.proofStatus === "missing") {
    return "Package proof not uploaded";
  }
  if (row.readinessStatus === "approved" || row.proofStatus === "approved") {
    return "Complete package approved.";
  }
  return lifecycleLabel(row);
};

const buildPackageDetailLine = (validation) => {
  const row = getPackageLifecycleRow(validation);
  const count = countArtworkLocations(validation);
  if (!row || !count) return null;
  if (row.readinessStatus === "proof_not_uploaded" || row.proofStatus === "missing") {
    return "No complete-package proof has been uploaded yet.";
  }
  if (row.readinessStatus === "approved" || row.proofStatus === "approved") {
    return "Internal approval complete Â· Customer approval complete";
  }
  return lifecycleDetail(row);
};

const packageCoverageDetail = (proof = {}) => {
  if (proof.readinessStatus === "approved" || proof.proofStatus === "approved") {
    return "Approved";
  }
  if (proof.readinessStatus === "proof_not_uploaded" || proof.proofStatus === "missing") {
    return "Package proof not uploaded";
  }
  if (proof.readinessStatus === "awaiting_customer_approval") {
    return "Awaiting customer approval";
  }
  if (proof.readinessStatus === "awaiting_internal_approval") {
    return "Awaiting internal approval";
  }
  if (proof.readinessStatus === "rejected") {
    return "Replacement required";
  }
  return "Awaiting approval";
};

const buildPolicyLine = ({ mode, validation }) => {
  const summary = validation?.summary || {};
  if (!summary.requiredCount) return null;
  if (validation?.valid) {
    return mode === PROOF_ENFORCEMENT_MODES.AT_LEAST_ONE
      ? "The current at least one proof requirement has been satisfied."
      : "All required proofs are approved.";
  }
  if (mode === PROOF_ENFORCEMENT_MODES.AT_LEAST_ONE) {
    return `At least ${summary.targetApprovalCount || 1} approval is required.`;
  }
  const remaining = summary.remainingRequiredCount ?? (Array.isArray(validation?.issues) ? validation.issues.length : 0);
  return `${remaining} required proof${remaining === 1 ? "" : "s"} still need approval before Purchase Orders can be generated.`;
};

const getIssueRows = (validation, mode) => {
  const proofs = Array.isArray(validation?.proofs) ? validation.proofs : [];
  if (isCompletePackageValidation(validation)) {
    return proofs.filter((proof) => proof?.required);
  }
  if (mode === PROOF_ENFORCEMENT_MODES.AT_LEAST_ONE) {
    return proofs.filter((proof) => proof?.required);
  }
  if (proofs.length) {
    return proofs.filter((proof) => proof?.required && !proof?.satisfied);
  }
  return Array.isArray(validation?.issues) ? validation.issues : [];
};

const formatPrimaryLabel = (proof = {}) => {
  const parts = [proof.location || proof.decorationName, proof.method].filter(Boolean);
  if (parts.length) return parts.join(" · ");
  if (proof.productName) return proof.productName;
  if (proof.decorationId) return `Decoration #${proof.decorationId}`;
  return "Proof requirement";
};

const PurchaseOrderProofEnforcementNotice = ({
  mode,
  validation = null,
  status,
  error,
  onRetry,
  hideReady = false,
}) => {
  if (status === "error") {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        <Typography variant="body2">{error || "Proof readiness could not be checked."}</Typography>
        {onRetry ? <Button size="small" onClick={onRetry}>Retry proof check</Button> : null}
      </Alert>
    );
  }

  if (status === "loading" || status === "idle") return null;

  if (validation?.evaluationScope === "purchase_order") {
    const groups = Array.isArray(validation?.groups) ? validation.groups : [];
    const summary = validation?.summary || {};
    const severity =
      status === "blocked" ? "error" :
      status === "partial" ? "warning" :
      "success";
    const heading =
      status === "complete" ? "Purchase Orders — Complete" :
      status === "partial" ? "Purchase Orders — Partially Ready" :
      status === "blocked" ? "Purchase Orders — Waiting on Requirements" :
      "Purchase Orders — Ready";
    const shippingProblems = groups.flatMap((group) =>
      group?.alreadyGenerated ? [] : (Array.isArray(group?.shippingIssues) ? group.shippingIssues : [])
    );
    const summaryText =
      status === "complete"
        ? "All Purchase Orders have already been generated."
        : `${Number(summary.readyToCreatePurchaseOrderCount) || 0} ready to generate · ${Number(summary.blockedPurchaseOrderCount) || 0} waiting on requirements · ${Number(summary.alreadyGeneratedPurchaseOrderCount) || 0} already generated.`;

    return (
      <Alert severity={severity} sx={{ mt: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>{heading}</Typography>
        <Typography variant="body2">{summaryText}</Typography>
        {shippingProblems.length > 0 ? (
          <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "text.secondary" }}>
            Shipping needs attention on {groups.filter((group) => !group.alreadyGenerated && group.shippingReady === false).length} PO(s). Review the product details under each PO below, then complete them in the Shipping tab.
          </Typography>
        ) : null}
        {groups.length ? (
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            {groups.map((group, index) => {
              const label =
                group?.supplierName ||
                group?.routeSummary ||
                group?.existingPoNumber ||
                `PO candidate ${index + 1}`;
              const poType = String(group?.type || "");
              // A supplier goods PO can have no decoration of its own while the
              // same approved line has a separate decorator PO. Match on the
              // approved OrderItem IDs, not on the supplier name.
              const linkedDecoratorGroups = poType === "No_decoration"
                ? groups.filter((candidate) =>
                    candidate !== group &&
                    String(candidate?.type || "") === "Different_supplier_decoration" &&
                    (candidate.orderItemIds || []).some((itemId) =>
                      (group.orderItemIds || []).some((ownId) => String(ownId) === String(itemId))
                    )
                  )
                : [];
              const linkedDecoratorNames = [...new Set(linkedDecoratorGroups.map((candidate) =>
                candidate.supplierName || (candidate.isEliteDecoratorGroup ? "Elite Decoration" : "Separate decorator")
              ))];
              const decorationDetail = poType === "Same_supplier_decoration"
                ? (group?.disposition === "supplier_proof_source"
                  ? "Supplier-managed decoration · Artwork / supplier proof to follow"
                  : "Supplier-managed decoration")
                : poType === "Different_supplier_decoration"
                  ? (group?.isEliteDecoratorGroup
                    ? "Internal Elite decoration · Artwork approval required"
                    : group?.isDecorationNetworkGroup
                      ? "Decoration Network · Separate decorator PO"
                      : "External decoration · Separate decorator PO")
                  : poType === "No_decoration"
                    ? (linkedDecoratorNames.length
                      ? `Products only · Decoration handled separately by ${linkedDecoratorNames.join(", ")}`
                      : "Products only · No decoration on this PO")
                    : poType === "Other_service_art"
                      ? "Artwork service"
                      : null;
              const stateLabel =
                group?.alreadyGenerated
                  ? `Already generated${group?.existingPoNumber ? ` · ${group.existingPoNumber}` : ""}`
                  : group?.routingReady === false
                    ? "Waiting on decorator routing"
                  : group?.shippingReady === false
                    ? "Waiting on shipping"
                  : group?.disposition === "supplier_proof_source"
                    ? "Will generate · Supplier proof to follow"
                    : group?.shouldGenerate
                      ? "Will generate now"
                      : "Waiting on proof";
              const proofDisposition = String(group?.disposition || "");
              const proofExplanation = group?.proofReady === false
                ? "Waiting on approval"
                : proofDisposition === "supplier_proof_source"
                  ? "Permitted · Supplier proof to follow"
                  : proofDisposition === "artwork_pending_exception"
                    ? "Permitted · Artwork pending exception"
                    : proofDisposition === "no_proof_required"
                      ? "No proof required"
                      : proofDisposition === "proof_ready"
                        ? "Approved · Ready"
                        : poType === "Same_supplier_decoration"
                          ? "Proof gate satisfied · Verify supplier artwork workflow"
                          : "Proof gate satisfied";
              const supplierNextStep = poType === "Same_supplier_decoration" && proofDisposition === "supplier_proof_source"
                ? (group?.shippingReady === false
                  ? "Next: Complete shipping → Generate PO → Obtain supplier proof"
                  : "Next: Generate PO → Obtain supplier proof")
                : null;
              const readyForGeneration = !group?.alreadyGenerated && group?.shouldGenerate === true;
              const proofBlocked = group?.proofReady === false;
              const shippingBlocked = group?.shippingReady === false;
              const routingBlocked = group?.routingReady === false;
              const exceptions = Array.isArray(group?.proofExceptionProducts) ? group.proofExceptionProducts : [];
              const proofIssues = Array.isArray(group?.proofIssues) ? group.proofIssues : [];
              return (
                <Box
                  key={group?.groupKey || index}
                  sx={{
                    border: "1px solid",
                    borderColor: readyForGeneration ? "success.main" : "divider",
                    borderLeft: "3px solid",
                    borderLeftColor: readyForGeneration ? "success.main" : group?.alreadyGenerated ? "text.disabled" : "warning.main",
                    bgcolor: "background.paper",
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 1.25,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    gap: 0.75,
                  }}
                >
                  <Typography variant="body2" sx={{ minWidth: 0, fontWeight: 700 }}>
                    {label}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                      PO {index + 1}
                    </Typography>
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      flexShrink: 0,
                      fontWeight: 700,
                      color: group?.alreadyGenerated
                        ? "success.main"
                        : readyForGeneration
                          ? "success.dark"
                          : group?.shouldGenerate
                            ? "success.main"
                            : "warning.main",
                    }}
                  >
                    {stateLabel}
                  </Typography>
                  {decorationDetail && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", flexBasis: "100%" }}>
                      {decorationDetail}
                    </Typography>
                  )}
                  {!group?.alreadyGenerated && (
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ width: "100%" }}>
                      <Typography variant="caption" sx={{ color: shippingBlocked ? "warning.dark" : "success.dark", fontWeight: 700 }}>
                        Shipping: {shippingBlocked ? "Needs attention" : "Ready"}
                      </Typography>
                      {poType === "No_decoration" ? (
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Artwork proofs: Handled {linkedDecoratorNames.length ? "on decorator PO" : "outside this goods-only PO / not applicable"}
                        </Typography>
                      ) : (
                        <Typography variant="caption" sx={{ color: proofBlocked ? "warning.dark" : "success.dark", fontWeight: 700 }}>
                          PO proof gate: {proofExplanation}
                        </Typography>
                      )}
                    </Stack>
                  )}
                  {!group?.alreadyGenerated && routingBlocked && (
                    <Box sx={{ width: "100%" }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "warning.dark" }}>
                        Supplier → Decorator shipping: Needs attention
                      </Typography>
                      {(group.routingIssues || []).map((issue, issueIndex) => (
                        <Typography key={issue.orderItemId || issueIndex} variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {(() => {
                            // Resolve only against the same PO candidate and exact order-item identity.
                            // Never use product ID/name matching: repeated products can be separate lines.
                            const relatedEntries = [
                              ...(group.shippingIssues || []),
                              ...(group.artworkExceptionStatusByProduct || []),
                              ...(group.proofExceptionProducts || []),
                              ...(group.proofIssues || []),
                            ];
                            const product = relatedEntries.find((entry) =>
                              issue.orderItemId != null &&
                              entry?.orderItemId != null &&
                              String(entry.orderItemId) === String(issue.orderItemId) &&
                              typeof entry.productName === "string" &&
                              entry.productName.trim()
                            );
                            const productName = issue.productName || product?.productName || "Product name unavailable";
                            const routeMissing = issue.missingFields?.includes("decoratorShippingRoute");
                            const destinationMissing = issue.missingFields?.includes("decoratorShipTo");
                            const problem = routeMissing && destinationMissing
                              ? "Select Ship To: Decorator, then complete the decorator shipping address and onward delivery destination in Shipping"
                              : routeMissing
                                ? "Select Ship To: Decorator and its address in Shipping"
                                : "Select the decorator onward delivery destination in Shipping";
                            return `${productName} — ${problem}`;
                          })()}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {!group?.alreadyGenerated && Array.isArray(group?.artworkExceptionStatusByProduct) && group.artworkExceptionStatusByProduct.length > 0 && poType === "Different_supplier_decoration" && (
                    <Box sx={{ width: "100%", border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                      <Typography variant="caption" sx={{ display: "block", fontWeight: 700, px: 1, py: 0.5, bgcolor: "action.hover" }}>
                        Artwork exception by product
                      </Typography>
                      {group.artworkExceptionStatusByProduct.map((entry, entryIndex) => (
                        <Box key={entry.orderItemId || entryIndex} sx={{ px: 1, py: 0.5, borderTop: "1px solid", borderColor: "divider", bgcolor: entry.enabled ? "rgba(27, 94, 32, 0.06)" : "background.paper" }}>
                          <Typography variant="caption" sx={{ display: "block", fontWeight: 600 }}>
                            {entry.productName}
                          </Typography>
                          <Typography variant="caption" sx={{ display: "block", color: entry.enabled ? "#1b5e20" : "warning.dark", fontWeight: 700 }}>
                            {entry.enabled ? "Artwork pending exception enabled" : proofBlocked ? "No artwork exception — approval may still be required" : "No artwork exception — proof requirement otherwise satisfied"}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                  {supplierNextStep && !group?.alreadyGenerated && (
                    <Typography variant="caption" color="text.secondary" sx={{ width: "100%", fontWeight: 500 }}>
                      {supplierNextStep}
                    </Typography>
                  )}
                  {!group?.alreadyGenerated && exceptions.length > 0 && poType !== "Different_supplier_decoration" && (
                    <Box sx={{ width: "100%", bgcolor: "rgba(27, 94, 32, 0.08)", border: "1px solid rgba(27, 94, 32, 0.16)", borderRadius: 1, px: 1, py: 0.5 }}>
                      {exceptions.map((entry, entryIndex) => (
                        <Typography key={entry.orderItemId || entryIndex} variant="caption" sx={{ display: "block", color: "#1b5e20", fontWeight: 700 }}>
                          Artwork pending exception enabled: {entry.productName}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {!group?.alreadyGenerated && proofBlocked && (
                    <Box sx={{ width: "100%" }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "warning.dark" }}>Proof requirements still outstanding</Typography>
                      {proofIssues.length ? proofIssues.map((issue, issueIndex) => (
                        <Typography key={issue.orderItemId || issueIndex} variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {issue.productName ? `${issue.productName}: ` : ""}{issue.reason}
                        </Typography>
                      )) : <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>Required artwork approvals are not complete for this PO.</Typography>}
                    </Box>
                  )}
                  {Array.isArray(group?.shippingIssues) && group.shippingIssues.length > 0 && !group.alreadyGenerated ? (
                    <Stack spacing={0.5} sx={{ width: "100%", pt: 0.5, mt: 0.25, borderTop: "1px solid", borderColor: "divider" }}>
                      {group.shippingIssues.map((issue, issueIndex) => (
                        <Box key={issue.orderItemId || issueIndex} sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                          <Box sx={{
                            width: 30, height: 30, flexShrink: 0, borderRadius: 1,
                            border: "1px solid", borderColor: "divider", overflow: "hidden",
                            bgcolor: "background.paper", display: "flex", alignItems: "center",
                            justifyContent: "center", fontSize: 10, color: "text.disabled",
                          }}>
                            {issue.imageUrl
                              ? <img src={issue.imageUrl} alt="" loading="lazy"
                                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                  onError={(event) => { event.currentTarget.style.display = "none"; }} />
                              : "—"}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" sx={{ display: "block", fontWeight: 600, lineHeight: 1.25 }}>
                              {issue.productName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.25 }}>
                              {issue.reason}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  ) : null}
                </Box>
              );
            })}
          </Stack>
        ) : null}
      </Alert>
    );
  }

  if (!mode || mode === PROOF_ENFORCEMENT_MODES.NONE) return null;

  const noProofsRequired = validation?.status === "not_required" ||
    (validation?.valid && (validation?.summary?.requiredCount || 0) === 0);
  if (noProofsRequired) {
    if (hideReady) return null;
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>Proofs</Typography>
        <Typography variant="body2">No proofs are required for this order.</Typography>
        <Typography variant="caption" sx={{ display: "block" }}>
          Not Required
        </Typography>
      </Alert>
    );
  }

  if (validation?.valid) {
    if (hideReady) return null;
    const completePackage = isCompletePackageValidation(validation);
    const policyLine = completePackage
      ? buildPackagePolicyLine({ mode, validation })
      : buildPolicyLine({ mode, validation });
    return (
      <Alert severity="success" sx={{ mt: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>Proofs — Ready</Typography>
        <Typography variant="body2">
          {completePackage ? "Complete package approved." : buildSummaryLine({ validation })}
        </Typography>
        {policyLine ? (
          <Typography variant="caption" sx={{ display: "block" }}>
            {policyLine}
          </Typography>
        ) : null}
      </Alert>
    );
  }

  if (!validation) return null;

  const issues = getIssueRows(validation, mode);
  const completePackage = isCompletePackageValidation(validation);
  const packageLocationCount = countArtworkLocations(validation);
  return (
    <Alert severity="error" sx={{ mt: 1 }}>
      <Typography variant="subtitle2" fontWeight={700}>
        Proofs — Needs attention
      </Typography>
      <Typography variant="body2">
        {completePackage
          ? "A complete-package proof is required before Purchase Orders can be generated."
          : mode === PROOF_ENFORCEMENT_MODES.AT_LEAST_ONE
          ? "At least one approved proof is required before Purchase Orders can be generated."
          : "Every required proof must be approved before Purchase Orders can be generated."}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.75, fontWeight: 700 }}>
        {completePackage
          ? `${artworkLocationText(packageLocationCount)} included in this proof package`
          : buildSummaryLine({ validation })}
      </Typography>
      <Typography variant="caption" sx={{ display: "block", mb: issues.length ? 1 : 0 }}>
        {completePackage
          ? buildPackageStatusLine(validation)
          : mode === PROOF_ENFORCEMENT_MODES.AT_LEAST_ONE
          ? (buildLifecycleSummary({ validation }) || "No qualifying approved proof was found for this order.")
          : (buildLifecycleSummary({ validation }) || buildPolicyLine({ mode, validation }))}
      </Typography>
      {completePackage && buildPackageDetailLine(validation) ? (
        <Typography variant="caption" sx={{ display: "block", mb: issues.length ? 1 : 0 }}>
          {buildPackageDetailLine(validation)}
        </Typography>
      ) : null}
      {completePackage && buildPackagePolicyLine({ mode, validation }) ? (
        <Typography variant="caption" sx={{ display: "block", mb: issues.length ? 1 : 0 }}>
          {buildPackagePolicyLine({ mode, validation })}
        </Typography>
      ) : null}
      <Stack spacing={1}>
        {issues.length ? (
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
            {completePackage
              ? "Included artwork"
              : mode === PROOF_ENFORCEMENT_MODES.AT_LEAST_ONE
                ? "Proof status"
                : "Proofs requiring attention"}
          </Typography>
        ) : null}
        {issues.map((issue, index) => (
          <Box key={`${issue.orderItemId || "order"}-${issue.decorationId || "decoration"}-${issue.chargeId || index}`}>
            <Typography variant="body2" fontWeight={700}>{formatPrimaryLabel(issue)}</Typography>
            {issue.artworkName ? <Typography variant="body2">{issue.artworkName}</Typography> : null}
            {!issue.artworkName && issue.productName ? <Typography variant="body2">{issue.productName}</Typography> : null}
            <Typography variant="body2">
              {completePackage && issue.coveredByPackage ? "Covered by complete package" : lifecycleLabel(issue)}
            </Typography>
            <Typography variant="caption" sx={{ display: "block" }}>
              {completePackage && issue.coveredByPackage ? packageCoverageDetail(issue) : lifecycleDetail(issue)}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Alert>
  );
};

export default PurchaseOrderProofEnforcementNotice;
